// src/components/templates/organisation/OrganisationOverview.jsx
import React, { useState, useEffect } from 'react';
import { 
  Building2, Users, MapPin, TrendingUp, Activity, Calendar, ClipboardList, FileText, Package, 
  Phone, Mail, Globe, ArrowRight, Database, AlertCircle, CheckCircle, AlertTriangle,
  Clock, Target, BarChart3, DollarSign, Timer, Zap
} from 'lucide-react';
import { organisationService } from '../../../services/organisationService';

export const OrganisationOverview = ({ 
  bookData, 
  formData, 
  managementConfig, 
  managementFeatures = {},
  onNavigate 
}) => {
  const [recentActivities, setRecentActivities] = useState([]);
  const [overviewStats, setOverviewStats] = useState({});
  const [loading, setLoading] = useState(true);

  const enabledFeatures = Object.keys(bookData?.managementFeatures || {}).filter(
    key => bookData.managementFeatures[key]?.enabled
  );

  useEffect(() => {
    loadOverviewData();
  }, [bookData?.id]);

  const loadOverviewData = async () => {
    if (!bookData?.id) return;

    try {
      setLoading(true);
      
      // Load recent activity records
      const recordsData = managementFeatures.records || {};
      const activities = recordsData.activities || [];
      
      // Get recent activities (last 5)
      const recent = activities
        .sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return dateB - dateA;
        })
        .slice(0, 5);
      
      setRecentActivities(recent);

      // Calculate overview statistics
      const stats = calculateOverviewStats();
      setOverviewStats(stats);
    } catch (error) {
      console.error('Error loading overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateOverviewStats = () => {
    const stats = {};
    
    // Process each enabled management feature
    enabledFeatures.forEach(feature => {
      const featureData = managementFeatures[feature];
      if (!featureData) return;

      switch (feature) {
        case 'team':
          stats.team = {
            total: featureData.totalMembers || featureData.members?.length || 0,
            active: featureData.activeMembers || featureData.members?.filter(m => m.isActive)?.length || 0
          };
          break;
        case 'sites':
          stats.sites = {
            total: featureData.totalSites || featureData.sites?.length || 0,
            active: featureData.activeSites || featureData.sites?.filter(s => s.status === 'active')?.length || 0
          };
          break;
        case 'assets':
          stats.assets = {
            total: featureData.totalAssets || featureData.assets?.length || 0,
            active: featureData.activeAssets || featureData.assets?.filter(a => a.status === 'active')?.length || 0
          };
          break;
        case 'tasks':
          stats.tasks = {
            total: featureData.totalTasks || featureData.tasks?.length || 0,
            completed: featureData.completedTasks || featureData.tasks?.filter(t => t.status === 'completed')?.length || 0
          };
          break;
        case 'records':
          const activities = featureData.activities || [];
          stats.records = {
            total: activities.length,
            green: activities.filter(a => a.status === 'green').length,
            orange: activities.filter(a => a.status === 'orange').length,
            red: activities.filter(a => a.status === 'red').length
          };
          break;
      }
    });

    return stats;
  };

  const StatusBadge = ({ status, size = 'sm' }) => {
    const statusConfig = {
      green: { label: 'On Track', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      orange: { label: 'Needs Attention', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
      red: { label: 'Critical Issue', color: 'bg-red-100 text-red-700', icon: AlertCircle }
    };
    
    const config = statusConfig[status] || statusConfig.green;
    const Icon = config.icon;
    const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';

    return (
      <span className={`inline-flex items-center gap-1 rounded-full font-medium ${config.color} ${sizeClasses}`}>
        <Icon className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'}`} />
        {config.label}
      </span>
    );
  };

  const MetricDisplay = ({ record }) => {
    const metrics = [];
    
    if (record.enabledMetrics?.duration && record.metrics?.actualDuration) {
      const hours = Math.floor(record.metrics.actualDuration / 60);
      const minutes = record.metrics.actualDuration % 60;
      metrics.push({
        icon: Clock,
        label: 'Duration',
        value: `${hours}h ${minutes}m`
      });
    }
    
    if (record.enabledMetrics?.quality && record.metrics?.qualityScore) {
      metrics.push({
        icon: Target,
        label: 'Quality',
        value: `${record.metrics.qualityScore}/10`
      });
    }
    
    if (record.enabledMetrics?.progress && record.metrics?.completionPercentage !== undefined) {
      metrics.push({
        icon: BarChart3,
        label: 'Progress',
        value: `${record.metrics.completionPercentage}%`
      });
    }
    
    if (record.enabledMetrics?.amount && record.metrics?.amountValue) {
      metrics.push({
        icon: DollarSign,
        label: 'Amount',
        value: `${record.metrics.amountCurrency}${record.metrics.amountValue}`
      });
    }

    return (
      <div className="flex gap-3 text-xs text-gray-500">
        {metrics.slice(0, 2).map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div key={index} className="flex items-center gap-1">
              <Icon className="h-3 w-3" />
              <span>{metric.value}</span>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 space-y-8">
        <div className="animate-pulse">
          <div className="bg-gray-200 h-40 rounded-xl mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-200 h-32 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      {/* Organisation Header */}
      <div className="bg-white rounded-xl shadow-sm border p-8">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-6">
            {bookData?.logoUrl ? (
              <img 
                src={bookData.faviconUrl} 
                alt="Organisation Logo"
                className="w-auto h-16 object-cover rounded-xl border"
              />
            ) : (
              <div className="w-20 h-20 bg-blue-100 rounded-xl flex items-center justify-center">
                <Building2 className="h-10 w-10 text-blue-600" />
              </div>
            )}
            
            <div>
             
              <p className="text-lg text-gray-600 mb-4">
                {bookData?.description || 'Organization Management Portal'}
              </p>
              
              <div className="flex items-center gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>Durban, KwaZulu-Natal</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>{enabledFeatures.length} Management Modules</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-900">
              {overviewStats.records?.total || 0}
            </div>
            <div className="text-sm text-gray-500">Total Activities</div>
            {overviewStats.records && (
              <div className="flex gap-2 mt-2">
                <span className="flex items-center gap-1 text-xs">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  {overviewStats.records.green}
                </span>
                <span className="flex items-center gap-1 text-xs">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  {overviewStats.records.orange}
                </span>
                <span className="flex items-center gap-1 text-xs">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  {overviewStats.records.red}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Access Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Dashboard Access */}
        <button
          onClick={() => onNavigate('dashboard')}
          className="bg-white rounded-xl shadow-sm border p-6 text-left hover:shadow-md transition-shadow group"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <BarChart3 className="h-6 w-6 text-blue-600" />
            </div>
            <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Management Dashboard</h3>
          <p className="text-gray-600">View analytics and insights across all management modules</p>
        </button>

        {/* Activity Records Access */}
        {enabledFeatures.includes('records') && (
          <button
            onClick={() => onNavigate('records')}
            className="bg-white rounded-xl shadow-sm border p-6 text-left hover:shadow-md transition-shadow group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gray-100 rounded-lg">
                <Database className="h-6 w-6 text-gray-600" />
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Activity Records</h3>
            <p className="text-gray-600">Track daily activities and performance metrics</p>
            {overviewStats.records && (
              <div className="mt-3 flex gap-1">
                <StatusBadge status="green" />
                <StatusBadge status="orange" />
                <StatusBadge status="red" />
              </div>
            )}
          </button>
        )}

        {/* Team Access */}
        {enabledFeatures.includes('team') && (
          <button
            onClick={() => onNavigate('team')}
            className="bg-white rounded-xl shadow-sm border p-6 text-left hover:shadow-md transition-shadow group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-green-600 transition-colors" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Team Management</h3>
            <p className="text-gray-600">Manage team members and organizational structure</p>
            {overviewStats.team && (
              <div className="mt-3 text-sm text-gray-500">
                {overviewStats.team.active}/{overviewStats.team.total} active members
              </div>
            )}
          </button>
        )}
      </div>

      {/* Management Features Grid */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Management Modules</h2>
          <span className="text-sm text-gray-500">{enabledFeatures.length} modules enabled</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {enabledFeatures.map(featureKey => {
            const FEATURE_CONFIG = {
              team: { icon: Users, color: 'blue', description: 'Manage team members and roles' },
              assets: { icon: Building2, color: 'green', description: 'Track facilities and equipment' },
              tasks: { icon: ClipboardList, color: 'orange', description: 'Organize projects and workflows' },
              sites: { icon: MapPin, color: 'pink', description: 'Manage multiple locations' },
              clients: { icon: Users, color: 'indigo', description: 'Customer relationship management' },
              documents: { icon: FileText, color: 'yellow', description: 'Document workflows and policies' },
              resources: { icon: Package, color: 'purple', description: 'Resource management and allocation' },
              records: { icon: Database, color: 'gray', description: 'Activity tracking and audit trails' }
            };

            const config = FEATURE_CONFIG[featureKey];
            if (!config) return null;

            const Icon = config.icon;
            const stats = overviewStats[featureKey];

            return (
              <button
                key={featureKey}
                onClick={() => onNavigate(featureKey)}
                className={`bg-white rounded-lg shadow-sm border p-4 text-left hover:shadow-md transition-all hover:border-${config.color}-200 group`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`p-2 bg-${config.color}-100 rounded-lg`}>
                    <Icon className={`h-5 w-5 text-${config.color}-600`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 capitalize">{featureKey}</h3>
                    {stats && (
                      <p className="text-xs text-gray-500">
                        {featureKey === 'records' 
                          ? `${stats.total} activities` 
                          : `${stats.active || stats.completed || 0}/${stats.total} active`
                        }
                      </p>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-600 group-hover:text-gray-700">
                  {config.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Recent Activities */}
      {recentActivities.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Recent Activities</h2>
            <button
              onClick={() => onNavigate('records')}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              View All
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-4">
            {recentActivities.map(activity => (
              <div key={activity.id} className="bg-white rounded-lg shadow-sm border p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900">{activity.title}</h3>
                      <StatusBadge status={activity.status} />
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {activity.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <MetricDisplay record={activity} />
                      <span className="text-xs text-gray-500">
                        {new Date(activity.createdAt?.toDate ? activity.createdAt.toDate() : activity.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};